import { supabase } from '../supabaseClient';
import { logAudit } from './auditLog';
import { isWorkItemNoSearch, parseWorkItemNoSearch } from '../constants/workItem';

// audit 不可讓寫入失敗 → 一律 guard(比照 workItems.js)
const safeAudit = async (...args) => {
  try { await logAudit(...args); } catch (e) { console.warn('[workItemReferences] audit failed', e); }
};

const REF_ITEM_SELECT = 'id, work_item_no, title, status, deleted_at, project_id, projects:project_id(name, color)';

/**
 * 關聯挑選器用的輕量搜尋。
 * 規則與全系統查詢 / 專案內篩選 / 我的工作項目一致(見 constants/workItem 的
 * isWorkItemNoSearch):**#編號 才是查編號**,純數字走文字搜尋,不然標題含數字的
 * 項目在這裡也一樣找不到。子項沒有 work_item_no,只會在文字搜尋時出現。
 */
export const searchWorkItemsForReference = async (query, companyId, { excludeIds = [], limit = 8 } = {}) => {
  if (!query || !companyId) return [];
  try {
    const exclude = new Set(excludeIds.map(Number));
    let dbQuery = supabase
      .from('work_items')
      .select(REF_ITEM_SELECT)
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .limit(limit + exclude.size);

    const term = String(query).trim();
    if (!term) return [];
    if (isWorkItemNoSearch(term)) {
      dbQuery = dbQuery.eq('work_item_no', parseWorkItemNoSearch(term)).order('work_item_no', { ascending: true });
    } else {
      const like = `%${term}%`;
      dbQuery = dbQuery
        .or(`title.ilike.${like},description.ilike.${like}`)
        .order('work_item_no', { ascending: false, nullsFirst: false });
    }

    const { data, error } = await dbQuery;
    if (error) throw error;
    return (data || []).filter((i) => !exclude.has(Number(i.id))).slice(0, limit);
  } catch (error) {
    console.error('Error searching work items for reference:', error);
    return [];
  }
};

/**
 * 取得某項目的所有關聯(正向 + 反向)。
 * 關聯是雙向呈現的:A 關聯 B 之後,兩邊的清單都看得到對方。
 */
export const getWorkItemReferences = async (itemId) => {
  if (!itemId) return { data: [], error: null };
  try {
    const [outgoing, incoming] = await Promise.all([
      supabase.from('work_item_references')
        .select(`id, target:work_items!work_item_references_target_item_id_fkey(${REF_ITEM_SELECT})`)
        .eq('source_item_id', itemId),
      supabase.from('work_item_references')
        .select(`id, source:work_items!work_item_references_source_item_id_fkey(${REF_ITEM_SELECT})`)
        .eq('target_item_id', itemId),
    ]);
    if (outgoing.error || incoming.error) throw outgoing.error || incoming.error;

    const rows = [
      ...(outgoing.data || []).map((r) => ({ refId: r.id, direction: 'outgoing', item: r.target })),
      ...(incoming.data || []).map((r) => ({ refId: r.id, direction: 'incoming', item: r.source })),
    ].filter((r) => r.item && !r.item.deleted_at);

    // 同一對項目正反向都建過時只留一筆,列表不重複
    const seen = new Set();
    const data = rows.filter((r) => {
      const key = Number(r.item.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return { data, error: null };
  } catch (error) {
    console.error('Error loading work item references:', error);
    return { data: [], error };
  }
};

/** 關聯在活動紀錄裡顯示成「#編號 標題」;子項沒有編號就只有標題。 */
export const referenceLabel = (item) => (item?.work_item_no != null ? `#${item.work_item_no} ${item.title || ''}`.trim() : (item?.title || ''));

export const addWorkItemReference = async (sourceItem, targetItem, { companyId, userId } = {}) => {
  const sourceId = Number(sourceItem?.id);
  const targetId = Number(targetItem?.id);
  if (!sourceId || !targetId) return { data: null, error: new Error('缺少項目') };
  if (sourceId === targetId) return { data: null, error: new Error('不能關聯自己') };
  try {
    const { data, error } = await supabase.from('work_item_references')
      .insert({ source_item_id: sourceId, target_item_id: targetId, company_id: companyId || null, created_by: userId || null })
      .select('id')
      .single();
    if (error) throw error;
    await safeAudit('update', 'work_item', sourceId, sourceItem?.title || null,
      { reference: { op: 'add', label: referenceLabel(targetItem) } }, null, null, companyId || null);
    return { data, error: null };
  } catch (error) {
    console.error('Error adding work item reference:', error);
    return { data: null, error };
  }
};

export const removeWorkItemReference = async (refId, { sourceItem, targetItem, companyId } = {}) => {
  if (!refId) return { error: new Error('缺少關聯 id') };
  try {
    const { error } = await supabase.from('work_item_references').delete().eq('id', refId);
    if (error) throw error;
    await safeAudit('update', 'work_item', Number(sourceItem?.id), sourceItem?.title || null,
      { reference: { op: 'remove', label: referenceLabel(targetItem) } }, null, null, companyId || null);
    return { error: null };
  } catch (error) {
    console.error('Error removing work item reference:', error);
    return { error };
  }
};
